import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Layers, Loader2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ProposalCardShell from './ProposalCardShell';
import SegmentRuleList from './SegmentRuleList';
import { segmentsApi } from '@/services/segments-api';
import { handleApiError } from '@/services/api';
import type { ToolSegmentPayload } from '@/types/chat';

type Proposal = Extract<ToolSegmentPayload, { type: 'segment_update_proposal' }>;

const SegmentUpdateProposalCard: React.FC<{ proposal: Proposal }> = ({ proposal }) => {
  const [dismissed, setDismissed] = useState(false);
  const [done, setDone] = useState(false);
  const { proposed, preview } = proposal;

  const approve = useMutation({
    mutationFn: () =>
      segmentsApi.updateAndConfirm(proposal.segment_id, {
        definition: proposed.definition,
        name: proposed.name,
      }),
    onSuccess: () => {
      setDone(true);
      toast.success('Segment updated');
    },
    onError: (error) => toast.error(handleApiError(error) || 'Failed to update segment'),
  });

  if (dismissed) {
    return (
      <ProposalCardShell icon={Layers} kicker="Segment update" title={proposed.name} actions={null} resolved={{ tone: 'muted', label: 'Dismissed' }}>
        <div />
      </ProposalCardShell>
    );
  }

  if (done) {
    return (
      <ProposalCardShell icon={Layers} kicker="Segment update" title={proposed.name} actions={null} resolved={{ tone: 'success', label: '✓ Segment updated' }}>
        <div />
      </ProposalCardShell>
    );
  }

  return (
    <ProposalCardShell
      icon={Layers}
      kicker="Segment update"
      title={proposed.name}
      subtitle={`key: ${proposal.key}`}
      actions={
        <>
          <button
            type="button"
            onClick={() => setDismissed(true)}
            disabled={approve.isPending}
            className="px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={() => approve.mutate()}
            disabled={approve.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {approve.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
            Approve &amp; update
          </button>
        </>
      }
    >
      {proposal.rationale && <p className="text-sm text-muted-foreground">{proposal.rationale}</p>}

      <div className="rounded-lg bg-muted dark:bg-white/[0.03] border border-border p-3 text-sm text-foreground font-medium">
        New rules match {preview.matched_items} item{preview.matched_items === 1 ? '' : 's'}
        {preview.weighted_count > 0 && (
          <span className="text-muted-foreground font-normal">
            {' '}
            ({preview.full_weight_count} full, {preview.weighted_count} split)
          </span>
        )}
      </div>

      <SegmentRuleList label="New match rules" rules={proposed.definition.match} />
      <SegmentRuleList label="New exclude rules" rules={proposed.definition.exclude ?? []} />
    </ProposalCardShell>
  );
};

export default SegmentUpdateProposalCard;
